/**
 *
 * @description 礼包领取服务V2，后续逐步重构原领取流程，原领取流程过于复杂
 *
 */
import { fetchReceiveGift, fetchReceiveLotteryPrize, lottery } from 'lib/apis';
import TipsModal from 'lib/components/Modal/TipsModal';
import { showModal } from 'lib/components/Modal';
import { AWARD_PRIZE_TYPE, DELIVER_STATUS } from 'lib/constants/award';
import { ERROR_MSG } from 'lib/constants/error';
import { getMobileGameRole } from 'lib/components/MobileGameRole';
import { GameRoleType } from 'lib/types/AMS';
import toast from 'lib/utils/toast';
import { showReceiveQBDialog } from 'lib/components/ReceiveQBDialog';
import { showTipsDialog } from 'lib/components/TipsDialog';
import { showCdkeyDialog } from 'lib/components/CdkeyDialog';
import { handleCMCCPrizeReceive } from 'lib/components/ReceiveDialog/helper';
import { getCdkeyDetail } from 'lib/utils/prize';
import { showOrderAddressInput } from 'lib/container/AddressInput';
import { ReceiveGiftRequestData } from 'lib/apis/model/gift';
import userOrderService from './userOrderService';
import channelService from './channelService';

const RECEIVE_ERROR_CODE = {
  CMCC_NO_AWARD_CHANCE: 30201,
  CMCC_LIMIT: 301000,
};

interface QBDeliverParams {
  qqUin: string;
}

class AwardPrizeServiceV2 {
  // 限时活动抽奖
  public async limitedActLottery({ actId, drawResult, successCallback }) {
    toast.loading();
    // 待领取状态分为两种，1.已中奖未领取，2.未中奖未领取
    if (drawResult.lotteryId) {
      // 已中奖
      toast.close();
      this.receiveLotteryGift({ actId, prizeInfo: drawResult, successCallback });
      return;
    }
    const res = await lottery({ actId });
    toast.close();
    if (res?.code !== 0) {
      this.handleReceiveGiftFail(res, () => {
        showTipsDialog({
          title: '抽奖失败',
          content: ERROR_MSG[res?.code] || '请稍后再试，若有疑问请咨询福利鹅微信公众号客服～',
        });
      });
      return;
    }
    successCallback?.();
    const prizeInfo = res?.data?.drawResult?.[0] || {};
    this.receiveLotteryGift({ actId, prizeInfo, successCallback });
  }

  // 限时任务必得礼包领取
  public async receiveLimitedTaskGift({ actId, prizeType, successCallback }) {
    switch (prizeType) {
      // 游戏道具
      case AWARD_PRIZE_TYPE.AMS: {
        const limitedTaskAmsPrizeDelivery = async (params) => {
          toast.loading();
          const res = await fetchReceiveGift({ ...params, actId });
          toast.close();
          if (res?.code !== 0) {
            this.handleReceiveGiftFail(res);
            return;
          }
          successCallback?.();
          TipsModal.show({
            title: '温馨提示',
            content: ['已成功领取礼包', '已发送至游戏邮箱，请在游戏内查收'],
            confirmText: '我知道了',
          });
        };
        this.displayMobileGameRole(limitedTaskAmsPrizeDelivery, {});
        return;
      }

      case AWARD_PRIZE_TYPE.QB: {
        const deliverRequest = async (params: QBDeliverParams) =>
          fetchReceiveGift({
            ...params,
            actId,
          });
        showReceiveQBDialog({ deliverRequest, receiveCallback: successCallback });
        return;
      }

      case AWARD_PRIZE_TYPE.CDKEY: {
        const limitedTaskCdkeyPrizeDelivery = async () => {
          toast.loading();
          const res = await fetchReceiveGift({ actId });
          toast.close();
          if (res?.code !== 0) {
            this.handleReceiveGiftFail(res, () => {
              showTipsDialog({
                title: '领取失败',
                content: ERROR_MSG[res?.code] || '请稍后再试，若有疑问请咨询福利鹅微信公众号客服～',
              });
            });
            return;
          }
          successCallback?.();
          const prizeInfo = res?.data.prize_groups?.[0]?.prizes?.[0] || {};
          const formatPrizeInfo = {
            ...prizeInfo,
            redeemCode: prizeInfo.redeem_code,
            redeemNo: prizeInfo.redeem_no,
            redeemtoken: prizeInfo.redeem_token,
          };
          const { redeemCode, redeemNo } = await getCdkeyDetail({ prizeInfo: formatPrizeInfo });
          if (!redeemCode) return;
          showCdkeyDialog({ redeemCode, redeemNo, callback: () => {} });
        };
        limitedTaskCdkeyPrizeDelivery();
        return;
      }

      case AWARD_PRIZE_TYPE.GOODS: {
        const receviedGoods = async (params: ReceiveGiftRequestData) =>
          fetchReceiveGift({ userInfo: params.userInfo, actId });
        showOrderAddressInput({ saveUserInfo: receviedGoods, successCallback });
        return;
      }
    }
  }

  public async checkPrizeDetail({ prizeInfo, prizeType }) {
    switch (prizeType) {
      case AWARD_PRIZE_TYPE.CDKEY: {
        const { redeemCode, redeemNo } = await getCdkeyDetail({ prizeInfo, failTitle: '查看失败' });
        if (!redeemCode) return;

        showCdkeyDialog({ redeemCode, redeemNo });
        return;
      }
    }
  }

  public handleReceiveGiftFail(result, showDefaultTips?: () => void) {
    const userOrderInfo = userOrderService.getUserOrderInfo();
    switch (result?.code) {
      case RECEIVE_ERROR_CODE.CMCC_NO_AWARD_CHANCE: {
        handleCMCCPrizeReceive(userOrderInfo?.orderMobile, channelService.gameCode, {
          isOverYear: userOrderInfo?.isOverYear,
        });
        break;
      }

      case RECEIVE_ERROR_CODE.CMCC_LIMIT: {
        handleCMCCPrizeReceive(userOrderInfo?.orderMobile, channelService.gameCode);
        break;
      }

      default: {
        if (!showDefaultTips) {
          TipsModal.show({
            title: '领取失败',
            content: ERROR_MSG[result?.code] || '请稍后重试',
          });
          return;
        }
        showDefaultTips();
      }
    }
  }

  private displayMobileGameRole(callback, params, isLottery?: boolean) {
    return showModal(
      getMobileGameRole({
        confirmCallback: (roleData: GameRoleType) => callback({ ...params, ...roleData }, isLottery),
      }),
    );
  }

  private receiveLotteryGift({ actId, prizeInfo, successCallback }) {
    const { prizeType, prizeName, lotteryId } = prizeInfo;
    switch (prizeType) {
      // 游戏道具
      case AWARD_PRIZE_TYPE.AMS: {
        const receiveGiftRequest = async (params) => {
          const res = await fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
          if (res?.code !== 0) {
            showTipsDialog({
              title: '领取失败',
              content: '请稍后再试，若有疑问请咨询福利鹅微信公众号客服～',
            });
            return;
          }
          successCallback?.();
          TipsModal.show({
            title: '温馨提示',
            content: ['已成功领取礼包', '已发送至游戏邮箱，请在游戏内查收'],
            confirmText: '我知道了',
          });
        };
        TipsModal.show({
          content: `恭喜您获得${prizeName}`,
          confirmCallback: () => this.displayMobileGameRole(receiveGiftRequest, {}, true),
        });
        return;
      }

      // 谢谢参与
      case AWARD_PRIZE_TYPE.NOTHING: {
        TipsModal.show({
          content: '很遗憾未抽中奖品，感谢您的参与',
        });
        return;
      }

      case AWARD_PRIZE_TYPE.QB: {
        const multipleLotteryQBPrizeDelivery = async (params: QBDeliverParams) =>
          fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
        showReceiveQBDialog({
          deliverRequest: multipleLotteryQBPrizeDelivery,
          receiveCallback: successCallback,
        });
        return;
      }
      case AWARD_PRIZE_TYPE.CDKEY: {
        const receiveCdkey = async () => {
          toast.loading();
          const formatPrizeInfo = { ...prizeInfo };
          const hasReceived = prizeInfo.deliverStatus === DELIVER_STATUS.DELIVERED;
          // draw cdkey为非加密的时候，抽奖结果为已发放，就不用调用去领取接口
          if (!hasReceived) {
            const res = await fetchReceiveLotteryPrize({ actId, lotteryId });
            toast.close();
            if (res?.code !== 0) {
              showTipsDialog({
                title: '领取失败',
                content: '请稍后再试，若有疑问请咨询福利鹅微信公众号客服～',
              });
              return;
            }
            formatPrizeInfo.redeemToken = res?.data.prize_groups?.[0]?.prizes?.[0]?.redeem_token;
          }
          successCallback?.();
          const { redeemCode, redeemNo } = await getCdkeyDetail({ prizeInfo: formatPrizeInfo });
          toast.close();
          showCdkeyDialog({ redeemCode, redeemNo, callback: successCallback });
          return;
        };
        receiveCdkey();
        break;
      }
      case AWARD_PRIZE_TYPE.GOODS: {
        showTipsDialog({
          title: '恭喜中奖',
          content: `恭喜您获得${prizeName}，请填写收货地址`,
          buttonText: '填写地址',
          onConfirm: () => {
            showOrderAddressInput({ ...prizeInfo, actId, successCallback });
          },
        });
        return;
      }
    }
  }
}

const awardPrizeServiceV2 = new AwardPrizeServiceV2();
export default awardPrizeServiceV2;
